import { Reservation } from "types/reservations";
import { ReservationShift } from "types/shifts";
import { blockToTime } from "utils/helper";

export const FULL_DAY_TABLE_BLOCK_START = 0;
export const FULL_DAY_TABLE_BLOCK_END = 112;

export type TableBlockTimeFields = {
  time: string;
  reservationLength: number;
  startTimeInBlocks: number;
  endTimeInBlocks: number;
};

export const FULL_DAY_TABLE_BLOCK_TIME_FIELDS: TableBlockTimeFields = {
  time: "00:00",
  reservationLength: FULL_DAY_TABLE_BLOCK_END - FULL_DAY_TABLE_BLOCK_START,
  startTimeInBlocks: FULL_DAY_TABLE_BLOCK_START,
  endTimeInBlocks: FULL_DAY_TABLE_BLOCK_END,
};

/**
 * Window a block has to cover so it blocks exactly one shift: the shift's full
 * start..close, deliberately NOT stitchShifts — its service cut trims a shift
 * as soon as another one starts inside its window, which is meant for
 * sequential shifts but truncates parallel ones (a Kino shift 10:00-00:00
 * would lose its last hour because the dinner shift starts at 17:30, leaving
 * 23:00 bookable on a blocked seat). Null when the shift has no usable window.
 */
export const shiftTableBlockTimeFields = (
  shifts: ReservationShift[],
  shiftId: string
): TableBlockTimeFields | null => {
  const shift = shifts.find((s) => s.id === shiftId);

  if (!shift || typeof shift.start !== "number" || typeof shift.close !== "number")
    return null;

  // Exactly the shift window, no rounding: the legacy outward rounding made a
  // block reach one slot into a neighbouring shift whenever a boundary sat on
  // :15/:45, which marked that whole neighbour shift as blocked.
  const start = shift.start;
  const close = shift.close;

  if (close <= start) return null;

  return {
    time: blockToTime(start),
    reservationLength: close - start,
    startTimeInBlocks: start,
    endTimeInBlocks: close,
  };
};

/**
 * Full-day detection has to survive the read path: useReservations rewrites
 * startTimeInBlocks from `time` on every snapshot, and the after-midnight
 * rule in timeToBlock turns the stored 0..112 into 96..208. Only the span is
 * stable in both representations, so detect by span, never by absolute start.
 */
export const isFullDayTableBlock = (block: Reservation) =>
  (block.endTimeInBlocks ?? 0) - (block.startTimeInBlocks ?? 0) >=
  FULL_DAY_TABLE_BLOCK_END - FULL_DAY_TABLE_BLOCK_START;

/**
 * Whether a block reaches into [start, close). Full-day blocks cover every
 * window regardless of their (rewritten, see above) absolute values; a block
 * without a readable window counts as covering, so a real block is never
 * silently hidden.
 */
export const tableBlockCoversWindow = (
  block: Reservation,
  start: number,
  close: number
) => {
  if (isFullDayTableBlock(block)) return true;

  if (
    !Number.isFinite(block.startTimeInBlocks) ||
    !Number.isFinite(block.endTimeInBlocks)
  ) {
    return true;
  }

  return block.startTimeInBlocks < close && block.endTimeInBlocks > start;
};

/** "10" for 10:00, "17:30" when the minutes matter. */
const shortBlockTime = (block: number) => {
  const time = blockToTime(block);
  return time.endsWith(":00") ? time.slice(0, 2) : time;
};

/**
 * Text for the lock marker on a table in the floor plan. Empty string means
 * "blocked, but show the lock alone" — a full-day block needs no time stamp,
 * only a range does.
 */
export const tableBlockPillLabel = (blocks: Reservation[]): string | null => {
  if (!blocks.length) return null;
  if (blocks.length > 1) return `${blocks.length} Sperren`;

  const [block] = blocks;
  if (isFullDayTableBlock(block)) return "";

  return `${shortBlockTime(block.startTimeInBlocks)}–${shortBlockTime(
    block.endTimeInBlocks
  )}`;
};

export const tableBlockLabel = (
  block: Reservation,
  shifts: ReservationShift[]
) => {
  if (isFullDayTableBlock(block)) return "Ganzer Tag";

  const range = `${blockToTime(block.startTimeInBlocks)} bis ${blockToTime(
    block.endTimeInBlocks
  )}`;

  const shift = shifts.find((s) => {
    if (!s.id) return false;
    const fields = shiftTableBlockTimeFields(shifts, s.id);
    return (
      fields?.startTimeInBlocks === block.startTimeInBlocks &&
      fields?.endTimeInBlocks === block.endTimeInBlocks
    );
  });

  return shift ? `${shift.name ?? shift.id} (${range})` : range;
};

/**
 * Whether a shift can put reservations on this table at all — the same
 * space/occasion rule the timetable's reservability grid uses. Shifts that
 * can't reach the table have no business in its block dialog.
 */
export const shiftBlockableForTable = (
  shift: ReservationShift,
  table: { space: string; occassions: string[] }
) => {
  if (shift.active === false || shift.disabled || shift.closed) return false;
  if (shift.commentOnly) return false;
  if (shift.type && shift.type !== 'reservation') return false;

  const spacesMatch = !shift.spaces?.length || shift.spaces.includes(table.space);
  const occassionsMatch =
    !shift.occassions?.length ||
    shift.occassions.some((o) => (table.occassions ?? []).includes(o));

  return shift.matchBoth
    ? spacesMatch && occassionsMatch
    : spacesMatch || occassionsMatch;
};

export type ScheduleBand = {
  key: string;
  kind: 'table' | 'space';
  start: number;
  close: number;
  label: string;
  /** Second line, e.g. that the space is closed under this table block. */
  note?: string;
  /** Set for table blocks: the document the band's remove button deletes. */
  blockId?: string;
};

/**
 * Bands for the table sidebar's time axis: where the table itself is blocked
 * and where its space is closed, clamped to the visible window. Replaces a
 * single centred "Gesperrt" text that never said which part of the day.
 */
export const tableScheduleBands = ({
  blocks,
  shifts,
  table,
  includeSpace,
  start,
  close,
}: {
  blocks: Reservation[];
  shifts: ReservationShift[];
  table: { space: string; occassions: string[] };
  includeSpace: boolean;
  start: number;
  close: number;
}): ScheduleBand[] => {
  const clamp = (from: number, to: number) => ({
    start: Math.max(from, start),
    close: Math.min(to, close),
  });

  const shiftNameFor = (from: number, to: number) =>
    shifts.find((s) => {
      if (!shiftBlockableForTable(s, table)) return false;
      const fields = s.id ? shiftTableBlockTimeFields(shifts, s.id) : null;
      return (
        fields?.startTimeInBlocks === from && fields?.endTimeInBlocks === to
      );
    })?.name;

  const tableBands: ScheduleBand[] = blocks.map((block) => {
    // Same rule as tableBlockCoversWindow: an unreadable window covers all.
    if (
      isFullDayTableBlock(block) ||
      !Number.isFinite(block.startTimeInBlocks) ||
      !Number.isFinite(block.endTimeInBlocks)
    ) {
      return {
        key: block.id,
        kind: 'table',
        start,
        close,
        label: 'Ganzer Tag gesperrt',
        blockId: block.id,
      };
    }

    const name = shiftNameFor(block.startTimeInBlocks, block.endTimeInBlocks);

    return {
      key: block.id,
      kind: 'table',
      ...clamp(block.startTimeInBlocks, block.endTimeInBlocks),
      label: name
        ? `${name} gesperrt`
        : `Gesperrt ${blockToTime(block.startTimeInBlocks)} bis ${blockToTime(
            block.endTimeInBlocks
          )}`,
      blockId: block.id,
    };
  });

  // Parallel shifts close the same hours, so overlapping windows become one
  // band naming all of them instead of stacked bands with stacked labels.
  const spaceWindows: {
    shifts: ReservationShift[];
    start: number;
    close: number;
  }[] = [];

  if (includeSpace) {
    shifts
      .filter(
        (s) =>
          !!s.overwrite?.closedSpaces?.includes(table.space) &&
          shiftBlockableForTable(s, table)
      )
      .map((s) => ({ shift: s, fields: shiftTableBlockTimeFields(shifts, s.id) }))
      .filter(
        (entry): entry is { shift: ReservationShift; fields: TableBlockTimeFields } =>
          !!entry.fields
      )
      .map(({ shift, fields }) => ({
        shift,
        ...clamp(fields.startTimeInBlocks, fields.endTimeInBlocks),
      }))
      .filter((w) => w.close > w.start)
      .sort((a, b) => a.start - b.start)
      .forEach((w) => {
        const last = spaceWindows[spaceWindows.length - 1];
        if (last && w.start < last.close) {
          last.close = Math.max(last.close, w.close);
          last.shifts.push(w.shift);
        } else {
          spaceWindows.push({ shifts: [w.shift], start: w.start, close: w.close });
        }
      });
  }

  // Bands are translucent, so a space band under a table band would show
  // through and both labels would overlap. The table band carries the closed
  // space as its own line, the space band is only drawn where no table
  // block lies.
  const labelled = tableBands.map((band) =>
    spaceWindows.some((s) => s.start < band.close && s.close > band.start)
      ? { ...band, note: 'Bereich zu' }
      : band
  );

  const withoutTableBlocks = (from: number, to: number) =>
    tableBands.reduce(
      (parts, t) =>
        parts.flatMap(([a, b]) =>
          t.close <= a || t.start >= b
            ? [[a, b]]
            : [
                [a, Math.min(t.start, b)],
                [Math.max(t.close, a), b],
              ].filter(([x, y]) => y > x)
        ),
      [[from, to]]
    );

  const spaceBands: ScheduleBand[] = spaceWindows.flatMap((w) =>
    withoutTableBlocks(w.start, w.close).map(([from, to], i) => ({
      key: `space-${w.shifts[0].id}-${i}`,
      kind: 'space' as const,
      start: from,
      close: to,
      label: `Bereich zu · ${w.shifts.map((s) => s.name ?? s.id).join(', ')}`,
    }))
  );

  return [...spaceBands, ...labelled].filter((b) => b.close > b.start);
};

export const tableBlockWindowKey = (
  window: Pick<TableBlockTimeFields, 'startTimeInBlocks' | 'endTimeInBlocks'>
) => `${window.startTimeInBlocks}-${window.endTimeInBlocks}`;

export const tableBlockDocumentId = ({
  restaurantId,
  date,
  tableName,
  timeFields,
}: {
  restaurantId: string;
  date: string;
  tableName: string;
  timeFields: TableBlockTimeFields;
}) =>
  encodeURIComponent(
    JSON.stringify([
      "table-block",
      restaurantId,
      date,
      tableName,
      tableBlockWindowKey(timeFields),
    ])
  );

/**
 * Existing blocks of a table, sorted into what the timeline can show: the
 * full-day block, the block covering each shift's window, and everything else
 * (a second block on the same window, or the table plan assistant's own
 * windows) so those stay removable instead of silently disappearing.
 *
 * A block is a time window, not a shift membership: shifts that share a window
 * (same times, different occasions) therefore share one block document.
 */
export const groupTableBlocks = (
  blocks: Reservation[],
  shifts: ReservationShift[],
  // With a table given, only shifts that can reach it count as "shift blocks";
  // windows still come from the FULL day list so the service cut stays stable.
  table?: { space: string; occassions: string[] } | null
) => {
  const shiftWindows = shifts
    .filter((s) => !!s.id && (!table || shiftBlockableForTable(s, table)))
    .map((s) => ({ shift: s, fields: shiftTableBlockTimeFields(shifts, s.id) }))
    .filter(
      (entry): entry is { shift: ReservationShift; fields: TableBlockTimeFields } =>
        !!entry.fields
    );

  let fullDay: Reservation | null = null;
  const byWindowKey: Record<string, Reservation> = {};
  const byShiftId: Record<string, Reservation> = {};
  const other: Reservation[] = [];

  blocks.forEach((block) => {
    if (isFullDayTableBlock(block)) {
      if (!fullDay) fullDay = block;
      else other.push(block);
      return;
    }

    const key = tableBlockWindowKey(block);
    const coversAShift = shiftWindows.some(
      (entry) => tableBlockWindowKey(entry.fields) === key
    );

    if (coversAShift && !byWindowKey[key]) byWindowKey[key] = block;
    else other.push(block);
  });

  shiftWindows.forEach(({ shift, fields }) => {
    const block = byWindowKey[tableBlockWindowKey(fields)];
    if (block) byShiftId[shift.id] = block;
  });

  return { fullDay: fullDay as Reservation | null, byWindowKey, byShiftId, other };
};

export const buildTableBlockDoc = ({
  restaurantId,
  date,
  tableName,
  space,
  uid,
  now,
  timeFields,
}: {
  restaurantId: string;
  date: string;
  tableName: string;
  space?: string;
  uid: string;
  now: number;
  timeFields: TableBlockTimeFields;
}) => ({
  guest: {
    email: "",
    phone: "",
    name: "Blockiert",
    comment: "",
    attr: [],
  },
  source: "InHouse",
  restaurant: restaurantId,
  space,
  date,
  tables: [tableName],
  guests: 0,
  fixed: true,
  ...timeFields,
  createdAt: 10000000000000,
  createdBy: uid,
  updatedAt: now,
  updatedBy: uid,
  blocked: true,
  blockedFullShift: true,
  status: "success",
});
